/**
 * NoPing app store links of a country/language pair page (/<geo>/<lang>/...): the App Store storefront of the pair's
 * country and the Google Play listing in the pair's language and country (owner rule 07/10/2026). Client-safe, no deps.
 *
 * - App Store: https://apps.apple.com/<cc>/app/noping/id6711342901. Checked 07/10/2026 for every geo of the manifest: the
 *   app page answers 200 in each storefront except Bangladesh and Iran, which have no App Store (Apple redirects them to
 *   /us/), so those two link to the US storefront directly.
 * - Google Play: https://play.google.com/store/apps/details?id=com.noping.gaming.vpn&hl=<lang>&gl=<CC>; other query
 *   parameters of the authored URL (e.g. an install referrer) are kept.
 * Any other URL (another app, another host) is not a NoPing store link: `localizeStoreHref` returns null.
 */

const APP_STORE_HOST = 'apps.apple.com';
const APP_STORE_ID = 'id6711342901';
const APP_STORE_PATH = /^\/(?:[a-z]{2}\/)?app\/[^/]+\/id6711342901\/?$/i;
const PLAY_HOST = 'play.google.com';
const PLAY_PATH = '/store/apps/details';
const PLAY_PACKAGE = 'com.noping.gaming.vpn';

/** Countries without an App Store storefront (Apple redirects them to the US store). */
const NO_APP_STORE_STOREFRONT: ReadonlySet<string> = new Set(['bd', 'ir']);
const APP_STORE_FALLBACK_COUNTRY = 'us';

/** Pair geos that are a subdivision of a country (/gb-sct/en): the stores of the country. */
const GEO_COUNTRY: Readonly<Record<string, string>> = { 'gb-sct': 'gb' };

/** Pair language segment → Google Play `hl` where the base subtag is not enough (Chinese scripts, Cantonese). */
const PLAY_LANGUAGE: Readonly<Record<string, string>> = { 'zh-hans': 'zh-CN', 'zh-hant': 'zh-TW', yue: 'zh-HK', 'yue-hant': 'zh-HK' };

export interface PairStoreLocale {
  /** ISO 3166-1 alpha-2 country, lower case (us, sa, gb). */
  country: string;
  /** Pair language segment, lower case (en, ar, zh-hans). */
  language: string;
}

/** Country and language of a pair prefix ('/us/en' → us/en, '/gb-sct/gd' → gb/gd), or null for anything else. */
export function pairStoreLocale(prefix: string): PairStoreLocale | null {
  const match = /^\/([a-z]{2}(?:-[a-z]+)?)\/([a-z]{2,3}(?:-[a-z]+)?)\/?$/i.exec(prefix.trim());
  if (!match) return null;
  const geo = (match[1] ?? '').toLowerCase();
  const country = GEO_COUNTRY[geo] ?? geo.slice(0, 2);
  return { country, language: (match[2] ?? '').toLowerCase() };
}

/** App Store page of NoPing in the pair country's storefront. */
export function appStoreHref(locale: PairStoreLocale): string {
  const storefront = NO_APP_STORE_STOREFRONT.has(locale.country) ? APP_STORE_FALLBACK_COUNTRY : locale.country;
  return `https://${APP_STORE_HOST}/${storefront}/app/noping/${APP_STORE_ID}`;
}

/** Google Play `hl` of a pair language: zh-hans → zh-CN, pt in Brazil → pt-BR, otherwise the base subtag (ar, ja, en). */
export function playLanguage(locale: PairStoreLocale): string {
  const mapped = PLAY_LANGUAGE[locale.language];
  if (mapped) return mapped;
  const base = locale.language.split('-')[0] ?? locale.language;
  if (base === 'pt') return locale.country === 'br' ? 'pt-BR' : 'pt-PT';
  return base;
}

/** Google Play listing of NoPing in the pair's language and country; `extra` keeps other parameters (referrer...). */
export function googlePlayHref(locale: PairStoreLocale, extra?: URLSearchParams): string {
  const params = new URLSearchParams({ id: PLAY_PACKAGE });
  extra?.forEach((value, name) => {
    if (!['id', 'hl', 'gl'].includes(name)) params.append(name, value);
  });
  params.set('hl', playLanguage(locale));
  params.set('gl', locale.country.toUpperCase());
  return `https://${PLAY_HOST}${PLAY_PATH}?${params.toString()}`;
}

/** A NoPing App Store / Google Play URL rewritten for the pair (prefix '/sa/ar'); null when `href` is not one. */
export function localizeStoreHref(href: string, prefix: string): string | null {
  const locale = pairStoreLocale(prefix);
  if (!locale) return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.toLowerCase();
  if (host === APP_STORE_HOST && APP_STORE_PATH.test(url.pathname)) return appStoreHref(locale);
  if (host === PLAY_HOST && url.pathname.replace(/\/$/, '') === PLAY_PATH && url.searchParams.get('id') === PLAY_PACKAGE) return googlePlayHref(locale, url.searchParams);
  return null;
}
